import getURLSearchParam, { getURLSearchParams, genUrl } from './base/url'
import { mpWebviewMap } from './router/mp-const'
import Agent from './agent'
import { E_ProcessEnv, getProcessEnv } from './base/env'

const defaultQueryToStay = [
  /* UTM */
  'utm_source',
  'utm_medium',
  'utm_term',
  'utm_content',
  'utm_campaign',
  /* app */
  'version_name',
  'ci',
  /* phx */
  'phx_wake_up_type',
  'phx_wake_up_source',
  'phx_ad_delivery_id',
  'cube_id',
  'theme',
  'liteset',
  'swimlane',
  'liveid',
  /* appmock */
  '_mock_',
  '__lxvalidation',
  '_debug_',
  'talos-canary',
]

type QUERY = Record<string, string | number | boolean>

export function getStatisticQuery(srcUrl = window.location.href, queryToStay = defaultQueryToStay): QUERY {
  const srcURL = new URL(srcUrl)
  const query: QUERY = {}
  // 旧的 URL 不存在 URLSearchParams，需要兼容
  let srcSearchParams = srcURL.searchParams
  if (!srcSearchParams) {
    srcSearchParams = getURLSearchParams(srcURL.search)
  }
  for (const queryKey of queryToStay) {
    const queryValue = srcSearchParams.get(queryKey)
    if (queryValue) {
      query[queryKey] = queryValue
    }
  }
  return query
}

/**
 * [getStatisticUrl 获取添加埋点参数的url]
 * @param  {[String]} destUrl [目标url]
 * @param  {[String]} srcUrl [原始url]
 * @return {[String]}         [添加的埋点的url]
 */
export function getStatisticUrl(
  destUrl: string,
  srcUrl: string = window.location.href,
  queryToStay = defaultQueryToStay,
): string {
  const srcQuery = getStatisticQuery(srcUrl, queryToStay)
  // console.log(destUrl)
  const destURL = new URL(destUrl)
  let destSearchParams = destURL.searchParams
  if (!destSearchParams) {
    destSearchParams = getURLSearchParams(destURL.search)
  }
  for (const queryKey of queryToStay) {
    const origin = destSearchParams.get(queryKey)
    const queryValue = srcQuery[queryKey]
    if (!origin && queryValue) {
      destSearchParams.set(queryKey, String(queryValue))
    }
  }
  if (getProcessEnv(E_ProcessEnv.VUE_APP_PROJECT_NAME) && !destSearchParams.get('phx_from')) {
    destSearchParams.set(
      'phx_from',
      getProcessEnv(E_ProcessEnv.VUE_APP_PROJECT_NAME) +
        (window.FILENAME && window.FILENAME !== 'index' ? '_' + window.FILENAME : ''),
    )
  }
  return genUrl(destURL, destSearchParams)
}

// https://km.sankuai.com/page/150594596
const DEFAULT_WAKE_UP_TYPE = {
  i: 'h5_share',
  weixin: 'wx_share',
  pc: 'pc_share',
  phx: 'app_share',
  meituan: 'mtapp_share',
  dianping: 'dpapp_share',
  phoenix_wxapp: 'xcx_share',
}

export function getShareType() {
  if (Agent.pc) return DEFAULT_WAKE_UP_TYPE.pc
  if (Agent.phx) return DEFAULT_WAKE_UP_TYPE.phx
  if (Agent.meituan) return DEFAULT_WAKE_UP_TYPE.meituan
  if (Agent.dianping) return DEFAULT_WAKE_UP_TYPE.dianping
  if (Agent.phoenix_wxapp) return DEFAULT_WAKE_UP_TYPE.phoenix_wxapp
  if (Agent.weixin) return DEFAULT_WAKE_UP_TYPE.weixin
  // if (destSearchParams.get('phx_wake_up_type')) return destSearchParams.get('phx_wake_up_type')
  return DEFAULT_WAKE_UP_TYPE.i
}
/**
 * [getShareUrl 获取添加埋点参数的分享url，只保留指定的链接参数，避免将多余的参数分享出去]
 * @param  {[String]} destUrl [目标url]
 * @param  {[String]} srcUrl [原始url]
 * @return {[String]}         [添加的埋点的url]
 */
export function getShareUrl(destUrl: string = window.location.href, query?: QUERY): string {
  const destURL = new URL(destUrl)
  let destSearchParams = destURL.searchParams
  if (!destSearchParams) {
    destSearchParams = getURLSearchParams(destURL.search)
  }
  const newDestSearchParams = new URLSearchParams()
  ;['theme', 'liteset', 'swimlane', 'phx_env'].forEach((key) => {
    let origin: string | null = null
    if (key === 'theme') origin = destSearchParams.get(key)
    const value = getURLSearchParam(key)
    if (!origin && value) {
      newDestSearchParams.set(key, value)
    }
  })
  const pwut = getShareType()
  if (pwut) {
    newDestSearchParams.set('phx_wake_up_type', pwut)
  }
  const pwus = destSearchParams.get('phx_wake_up_source')
  const padi = destSearchParams.get('phx_ad_delivery_id')
  if (pwus || padi) {
    newDestSearchParams.set('phx_wake_up_source', pwus || padi || '')
  }
  if (query) {
    for (const key in query) {
      if (query[key]) newDestSearchParams.set(key, String(query[key]))
    }
  }
  return encodeURI(genUrl(destURL, newDestSearchParams))
}

/**
 * [getSharePath 小程序获取添加埋点参数的分享url，只保留指定的链接参数，避免将多余的参数分享出去]
 * @param  {[String]} shareUrl [目标url]
 * @return {[String]}         [添加的埋点的url]
 */
function getZhenguoSharePath(
  shareUrl: string,
  options: {
    hideShareMenu?: number
    fromIndex?: number
  } = {},
): string {
  const shareURL = new URL(shareUrl)
  let destSearchParams = shareURL.searchParams
  if (!destSearchParams) {
    destSearchParams = getURLSearchParams(shareURL.search)
  }
  const webQuery = [
    ['openUrl', encodeURIComponent(shareUrl)],
    ['t', destSearchParams.get('phx_wake_up_type')],
    ['s', destSearchParams.get('phx_wake_up_source')],
    ['hideShareMenu', options.hideShareMenu || 0],
  ]
    .filter((item) => item[1])
    .map((item) => `${item[0]}=${item[1]}`)
    .join('&')
  let sharePath = `/pages/web/web?${webQuery}`
  if (options.fromIndex) {
    const tsQuery = [
      ['t', destSearchParams.get('phx_wake_up_type')],
      ['s', destSearchParams.get('phx_wake_up_source')],
    ]
      .filter((item) => item[1])
      .map((item) => `${item[0]}=${item[1]}`)
      .join('&')
    sharePath = `/pages/index/index?navigateTo=${encodeURIComponent(sharePath)}&${tsQuery}`
  }
  // console.log('[sharePath]', sharePath)
  return sharePath
}

export function getSharePath(shareUrl: string, options = {}) {
  const zhenguoSharePath = getZhenguoSharePath(shareUrl, options)
  const webviewMap = mpWebviewMap(shareUrl)
  return {
    dianping_wxapp: webviewMap.dianping_wxapp,
    group_wxapp: webviewMap.group_wxapp,
    hotel_wxapp: webviewMap.hotel_wxapp,
    phoenix_wxapp: zhenguoSharePath,
  }
}
